import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartsDataView } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCarts from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCarts";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableButtonProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const getCartsApiParameter = state.pages.savedOrderList.getCartsApiParameter;
    // console.log("getCartsApiParameter", getCartsApiParameter);
    const savedCartsDataView = getCartsDataView(state, state.pages.savedOrderList.getCartsApiParameter);
    return {
        state,
        getCartsApiParameter,
        savedCartsDataView,
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
        savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadCarts,
    loadCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SupplyLandSavedOrdersStyles {
    container?: InjectableCss;
    titleGridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    viewAllGridItem?: GridItemProps;
    title?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderDateHeader?: DataTableHeaderProps;
    shipToLabelHeader?: DataTableHeaderProps;
    numberOfLinesHeader?: DataTableHeaderProps;
    subtotalHeader?: DataTableHeaderProps;
    goToCartHeader?: DataTableHeaderProps;
    orderDateCell?: DataTableCellProps;
    shipToLabelCell?: DataTableCellProps;
    numberOfLinesCell?: DataTableCellProps;
    subtotalCell?: DataTableCellProps;
    goToSavedOrderCell?: DataTableCellProps;
    goToSavedOrderButton?: ButtonPresentationProps;
}

export const supplyLandSavedOrdersStyles: SupplyLandSavedOrdersStyles = {
    container: {
        css: css`
            border: 1px solid #888;
            overflow: auto;
        `,
    },
    titleGridContainer: {
        css: css`
            padding-bottom: 20px;
        `,
        offsetCss: css`
            margin-bottom: 10px;
            border-bottom: 1px solid ${getColor("common.border")};
            &&& {
                width: 100%;
            }
        `,
    },
    titleGridItem: {
        width: 8,
        css: css`
            padding: 15px;
        `,
    },
    title: {
        css: css`
            margin-bottom: 1rem;
        `,
    },
    viewAllGridItem: {
        width: 4,
    },
    viewAllLink: {
        css: css`
            margin-left: auto;
            margin-top: 5px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: left;
        `,
    },
    noResultsText: {
        size: 15,
    },
    orderDateHeader: {
        css: css``,
    },
    numberOfLinesHeader: {
        css: css`
            text-align: left;
        `,
    },
    shipToLabelHeader: {
        css: css``,
    },
    subtotalHeader: {
        css: css`
            text-align: right;
        `,
    },
    goToCartHeader: {
        css: css`
            text-align: right;
        `,
    },
    numberOfLinesCell: {
        css: css`
            text-align: left;
        `,
    },
    shipToLabelCell: {
        typographyProps: {
            ellipsis: true,
        },
    },
    subtotalCell: {
        alignX: "right",
    },
    goToSavedOrderCell: {
        alignX: "right",
    },
    goToSavedOrderButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            padding: 0 11px;
            min-height: 30px;
        `,
    },
};

const styles = supplyLandSavedOrdersStyles;

class SupplyLandSavedOrders extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewOrders && !this.props.savedCartsDataView.value) {
            this.props.loadCarts({ apiParameter: this.props.getCartsApiParameter });
        }
    }

    render() {
        const { language, savedOrderDetailsLink, savedCartsDataView, state } = this.props;

        if (!savedCartsDataView.value) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                </StyledWrapper>
            );
        }

        const header = (
            <GridContainer {...styles.titleGridContainer}>
                <GridItem width={8}>
                    <Typography {...styles.title} variant="h2">
                        {translate("My Saved Orders")}
                    </Typography>
                </GridItem>
                <GridItem width={4}>
                    <Link
                        {...styles.viewAllLink}
                        href="/MyAccount/SavedOrders"
                        icon={{ iconProps: { src: ChevronRight } }}
                        typographyProps={{ size: 15 }}
                        data-test-selector="cardListHeadingViewAllLink"
                    >
                        {translate("View All")}
                    </Link>
                </GridItem>
            </GridContainer>
        );

        if (savedCartsDataView.value.length === 0) {
            return (
                <>
                    {header}
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...styles.noResultsText}>{translate("No Saved Orders Found")}</Typography>
                    </StyledWrapper>
                </>
            );
        }

        // console.log("savedCartsDataView", savedCartsDataView.value);

        const rows = savedCartsDataView.value.map(savedOrder => {
            return {
                id: savedOrder.id,
                date: savedOrder.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(savedOrder.orderDate),
                          language,
                      })
                    : "",
                shipToLabel: savedOrder.collectionShipToLabel ?? savedOrder.shipToLabel,
                numberOfCartLines: savedOrder.properties["customLineCount"],
                subtotal: savedOrder.orderSubTotalDisplay,
            };
        });

        return (
            <>
                {header}
                <StyledWrapper {...styles.container}>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.orderDateHeader}>{translate("Date")}</DataTableHeader>
                            <DataTableHeader {...styles.numberOfLinesHeader}>
                                {translate("Items in Cart")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.shipToLabelHeader}>{translate("Ship To")}</DataTableHeader>
                            <DataTableHeader {...styles.subtotalHeader}>{translate("Order Subtotal")}</DataTableHeader>
                            <DataTableHeader {...styles.goToCartHeader}></DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {rows.map(({ id, date, shipToLabel, numberOfCartLines, subtotal }) => (
                                <DataTableRow
                                    data-test-selector="savedOrderList_orderLine"
                                    data-test-key={id}
                                    key={id}
                                    {...styles.dataTableRow}
                                >
                                    <DataTableCell {...styles.orderDateCell}>
                                        <Link href={`${savedOrderDetailsLink?.url}?cartId=${id}`}>{date}</Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.numberOfLinesCell}>{numberOfCartLines}</DataTableCell>
                                    <DataTableCell {...styles.shipToLabelCell}>{shipToLabel}</DataTableCell>
                                    <DataTableCell {...styles.subtotalCell}>{subtotal}</DataTableCell>
                                    <DataTableCell {...styles.goToSavedOrderCell}>
                                        <Clickable href={`${savedOrderDetailsLink?.url}?cartId=${id}`}>
                                            <Button {...styles.goToSavedOrderButton}>{translate("View")}</Button>
                                        </Clickable>
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SupplyLandSavedOrders),
    definition: {
        group: "Saved Order List",
        displayName: "MyAccount Dashboard - Saved Orders",
        icon: "list-ul",
        allowedContexts: ["MyAccountPage"],
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Saved Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: "",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
